import { Helmet } from 'react-helmet-async';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e", card: "#13131a" };

const S = {
  h2: { color: COLORS.text, fontSize: '1.4rem', fontWeight: 800, margin: '40px 0 12px', letterSpacing: '-0.3px' },
  h3: { color: COLORS.text, fontSize: '1.1rem', fontWeight: 700, margin: '28px 0 8px' },
  p: { color: COLORS.muted, lineHeight: 1.75, marginBottom: 20 },
  strong: { color: COLORS.text },
  li: { color: COLORS.muted, lineHeight: 1.75, marginBottom: 10 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 28 },
  th: { color: COLORS.accent, fontSize: '0.8rem', letterSpacing: '1px', textTransform: 'uppercase', padding: '10px 14px', borderBottom: `2px solid ${COLORS.border}`, textAlign: 'left' },
  td: { color: COLORS.muted, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`, lineHeight: 1.5 },
  tdFirst: { color: COLORS.text, fontWeight: 600, padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}` },
  cta: { display: 'block', margin: '40px 0', padding: '18px 32px', background: COLORS.accent, color: '#000', fontWeight: 800, fontSize: '1rem', borderRadius: 10, textDecoration: 'none', textAlign: 'center', letterSpacing: '-0.2px' },
  callout: { background: COLORS.card, border: `1px solid ${COLORS.border}`, borderLeft: `3px solid ${COLORS.accent}`, borderRadius: 8, padding: '16px 20px', marginBottom: 24 },
};

const faqItems = [
  {
    q: 'How long does it take to pay off $10,000 in credit card debt?',
    a: 'On minimum payments only at 22% APR, a $10,000 balance takes roughly 15–17 years and costs over $12,000 in interest. With a fixed $300/month payment, it clears in about 4 years. With $500/month, about 2.5 years.',
  },
  {
    q: 'How long to pay off $30,000 in debt?',
    a: 'At $500/month with an average 18% APR, $30,000 takes about 9 years. At $1,000/month, roughly 4 years. The payoff calculator gives you an exact date for your specific balances and rates.',
  },
  {
    q: 'Does paying twice a month help?',
    a: 'Bi-weekly payments can save a small amount on daily-accrual debt, but the main effect is making 26 half-payments (equivalent to 13 full monthly payments) per year instead of 12 — that one extra payment per year makes a noticeable difference over time.',
  },
  {
    q: 'What is the minimum payment formula?',
    a: 'Most credit cards charge the higher of 1–3% of the balance or a flat minimum ($25–$35). At 2% of balance on a $5,000 card at 22% APR, your first minimum is $100 — but as the balance falls, so does the minimum, which is why minimum-only payoff takes so long.',
  },
  {
    q: 'How does my credit score affect payoff time?',
    a: 'Your credit score doesn\'t affect your existing loan terms, but a higher score can help you refinance or get a balance transfer card at 0% APR — which can dramatically cut payoff time by freezing interest while you pay down principal.',
  },
];

export default function HowLongToPayOffDebt() {
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <html lang="en" />
        <title>How Long Does It Take to Pay Off Debt? Real Numbers | ClearMyDebt</title>
        <meta name="description" content="How long does it take to pay off debt? Real payoff timelines for common balances at 15–25% APR — and what changes them fastest. Free calculator." />
        <link rel="canonical" href="https://clearmydebt.app/blog/how-long-to-pay-off-debt/" />
        <meta property="og:title" content="How Long Does It Take to Pay Off Debt? Real Numbers | ClearMyDebt" />
        <meta property="og:description" content="Real payoff timelines for common balances and APRs — and what changes them fastest. Free debt payoff calculator." />
        <meta property="og:url" content="https://clearmydebt.app/blog/how-long-to-pay-off-debt/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <link rel="alternate" hreflang="x-default" href="https://clearmydebt.app/blog/how-long-to-pay-off-debt/" />
        <link rel="alternate" hreflang="en" href="https://clearmydebt.app/blog/how-long-to-pay-off-debt/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "headline": "How Long Does It Take to Pay Off Debt? Real Numbers",
              "description": "Real payoff timelines for common balances and APRs — and what changes them fastest.",
              "url": "https://clearmydebt.app/blog/how-long-to-pay-off-debt/",
              "publisher": { "@type": "Organization", "name": "ClearMyDebt", "url": "https://clearmydebt.app" },
            },
            {
              "@type": "FAQPage",
              "mainEntity": faqItems.map(f => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            },
          ],
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '48px 24px 80px' }}>

        <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: 32 }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
          <span style={{ margin: '0 8px' }}>&#8250;</span>
          <span>Blog</span>
        </div>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: 16, textTransform: 'uppercase' }}>
            Debt Planning
          </div>
          <h1 style={{ fontSize: 'clamp(26px,5vw,40px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 16px', lineHeight: 1.15 }}>
            How Long Does It Take to Pay Off Debt?
          </h1>
          <p style={{ color: COLORS.muted, fontSize: '1.1rem', lineHeight: 1.6, margin: 0 }}>
            Real numbers for common balances &#8212; and the three variables that change your timeline the most.
          </p>
        </div>

        <p style={S.p}>
          The question people ask most often is not &#8220;how do I pay off debt&#8221; &#8212; it is
          &#8220;how long is this actually going to take?&#8221; The honest answer depends on three things:
          your balance, your interest rate, and what you pay each month. This article gives you
          real timelines for common scenarios so you know exactly where you stand.
        </p>
        <p style={S.p}>
          And if you want your specific answer with your exact numbers, use the free calculator below.
        </p>

        <a href="/" style={S.cta}>Get Your Exact Payoff Date &#8212; Free</a>

        <h2 style={S.h2}>Why Minimum Payments Are So Dangerous</h2>
        <p style={S.p}>
          Credit card minimum payments are designed to keep you in debt as long as possible.
          Most cards set your minimum at 1&#8211;2% of the balance or a flat rate &#8212; whichever is higher.
          As your balance drops, so does your minimum, which means each month you are making smaller
          payments just as interest is compounding.
        </p>

        <div style={S.callout}>
          <strong style={S.strong}>The trap:</strong>{" "}
          <span style={{ color: COLORS.muted }}>A $5,000 balance at 22% APR on minimum payments (2%) takes approximately 16 years to pay off and costs $7,500 in total interest — more than the original balance. The same debt at $200/month fixed clears in 2.5 years and costs $1,100 in interest.</span>
        </div>

        <h2 style={S.h2}>Credit Card Payoff Timelines</h2>
        <p style={S.p}>
          These figures assume a fixed monthly payment (not a declining minimum) at 22% APR.
        </p>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Balance</th>
              <th style={S.th}>$100/mo</th>
              <th style={S.th}>$200/mo</th>
              <th style={S.th}>$300/mo</th>
              <th style={S.th}>$500/mo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={S.tdFirst}>$2,000</td>
              <td style={S.td}>2.5 yrs</td>
              <td style={S.td}>11 mos</td>
              <td style={S.td}>8 mos</td>
              <td style={S.td}>4 mos</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$5,000</td>
              <td style={S.td}>9+ yrs</td>
              <td style={S.td}>2.5 yrs</td>
              <td style={S.td}>1.9 yrs</td>
              <td style={S.td}>11 mos</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$10,000</td>
              <td style={S.td}>—</td>
              <td style={S.td}>7+ yrs</td>
              <td style={S.td}>4 yrs</td>
              <td style={S.td}>2.5 yrs</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$20,000</td>
              <td style={S.td}>—</td>
              <td style={S.td}>—</td>
              <td style={S.td}>12+ yrs</td>
              <td style={S.td}>5.5 yrs</td>
            </tr>
          </tbody>
        </table>

        <p style={{ color: COLORS.muted, fontSize: '0.85rem', marginBottom: 28, fontStyle: 'italic' }}>
          Dashes indicate the payment doesn&#8217;t cover monthly interest at 22% APR (you would never pay off the debt).
        </p>

        <h2 style={S.h2}>Personal Loan Payoff Timelines</h2>
        <p style={S.p}>
          Personal loans typically use fixed payments, so the timeline is locked in at origination.
          Here are common scenarios at 15% APR.
        </p>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Loan balance</th>
              <th style={S.th}>3-year term</th>
              <th style={S.th}>5-year term</th>
              <th style={S.th}>Total interest (5yr)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={S.tdFirst}>$5,000</td>
              <td style={S.td}>$173/mo</td>
              <td style={S.td}>$119/mo</td>
              <td style={S.td}>~$2,100</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$10,000</td>
              <td style={S.td}>$347/mo</td>
              <td style={S.td}>$238/mo</td>
              <td style={S.td}>~$4,300</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$20,000</td>
              <td style={S.td}>$693/mo</td>
              <td style={S.td}>$476/mo</td>
              <td style={S.td}>~$8,600</td>
            </tr>
          </tbody>
        </table>

        <h2 style={S.h2}>Student Loan Payoff Timelines</h2>
        <p style={S.p}>
          Federal student loans default to 10-year standard repayment. Here is how that changes
          with extra payments at 6.5% APR.
        </p>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Balance</th>
              <th style={S.th}>Standard 10yr payment</th>
              <th style={S.th}>+$100 extra/mo</th>
              <th style={S.th}>+$200 extra/mo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={S.tdFirst}>$20,000</td>
              <td style={S.td}>$227/mo → 10 yrs</td>
              <td style={S.td}>7.5 yrs</td>
              <td style={S.td}>6 yrs</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$40,000</td>
              <td style={S.td}>$454/mo → 10 yrs</td>
              <td style={S.td}>7.5 yrs</td>
              <td style={S.td}>6 yrs</td>
            </tr>
            <tr>
              <td style={S.tdFirst}>$80,000</td>
              <td style={S.td}>$908/mo → 10 yrs</td>
              <td style={S.td}>8.5 yrs</td>
              <td style={S.td}>7.5 yrs</td>
            </tr>
          </tbody>
        </table>

        <h2 style={S.h2}>The Three Variables That Matter Most</h2>

        <h3 style={S.h3}>1. Monthly payment amount</h3>
        <p style={S.p}>
          This is the variable with the most leverage. Doubling your monthly payment typically
          cuts payoff time by more than half &#8212; because the interest portion of each payment
          shrinks faster as principal falls. Even $100&#8211;$150 extra per month moves the needle
          dramatically on balances under $10,000.
        </p>

        <h3 style={S.h3}>2. Interest rate</h3>
        <p style={S.p}>
          Every percentage point of APR matters. A $10,000 balance at 24% costs $8,100 more in
          interest than the same balance at 14% on a 5-year payoff plan. Refinancing, balance
          transfers, or calling to negotiate a rate reduction can shave years off your timeline.
        </p>

        <h3 style={S.h3}>3. Whether you add to the balance</h3>
        <p style={S.p}>
          Every dollar added to a credit card balance resets some of your progress &#8212; not just
          the principal but the interest that compounds on it. Freezing the balance (not using the
          card) is often the single most impactful first step.
        </p>

        <h2 style={S.h2}>Frequently Asked Questions</h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: COLORS.card, borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

        <div style={{ ...S.callout, marginTop: 40 }}>
          <p style={{ color: COLORS.text, fontWeight: 700, fontSize: '1.05rem', margin: '0 0 8px' }}>
            Get your exact payoff date
          </p>
          <p style={{ color: COLORS.muted, margin: '0 0 16px', lineHeight: 1.6 }}>
            Enter your actual debts &#8212; any number, any type &#8212; and the free calculator tells
            you exactly when you&#8217;ll be debt-free and how much interest you&#8217;ll save with
            different payment amounts.
          </p>
          <a href="/" style={{ ...S.cta, margin: 0 }}>Calculate Your Payoff Timeline</a>
        </div>

      </div>
    </div>
  );
}
